<template>
  <!-- ONE WAY INTO EVERY OTHER MODEL. Whatever the composer borrows — a whole model to copy, a year
       of sales, a season, a handful of channels — it is found here, the same way: by the model's
       name, by what the model is about (its country, client, brand, category) and, for channels, by
       what each channel is in the taxonomy. The three narrow one list together.

       Two modes. `model` asks for ONE model: a click on it is the answer, and a host that has more
       to ask about it (which KPI, which year) gets a second step in the same dialog through the
       `details` slot. `channels` asks for any number of channels out of any number of models, which
       arrive grouped under their model so a whole model can be ticked at once. The dialog owns the
       finding and the ticking and nothing else: reading a model and placing what it gives are the
       composer's. -->
  <v-dialog :value="value" width="1080" scrollable @input="$emit('input', $event)">
    <v-card
      class="clone-picker"
      @keydown.enter.meta.prevent="add"
      @keydown.enter.ctrl.prevent="add">
      <v-card-title class="clone-picker__title">
        <v-icon color="primary" class="mr-3">{{ icon }}</v-icon>
        <div class="clone-picker__heading">
          <span class="subtitle-1 font-weight-medium">{{ title }}</span>
          <span v-if="subtitle" class="clone-picker__subtitle">{{ subtitle }}</span>
        </div>
        <v-spacer></v-spacer>
        <v-btn icon small :title="$t('components.TextBox.close')" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- THE SECOND STEP: the model is chosen and the host asks what to take from it. The model
           stays named above the question, with the way back beside it. -->
      <template v-if="onDetails">
        <div class="clone-picker__bar imp-chosen">
          <v-btn small text color="primary" class="imp-chosen__back" @click="$emit('back')">
            <v-icon small left>mdi-arrow-left</v-icon>{{ t('importerChangeModel') }}
          </v-btn>
          <v-icon small class="imp-chosen__icon">mdi-database</v-icon>
          <div class="imp-chosen__body">
            <span class="imp-chosen__name text-truncate">{{ pickedRow ? pickedRow.name : pickedModelId }}</span>
            <span v-if="pickedRow && metaLine(pickedRow)" class="imp-meta text-truncate">{{ metaLine(pickedRow) }}</span>
          </div>
        </div>
        <v-card-text class="imp-details">
          <slot name="details"></slot>
        </v-card-text>
        <v-card-actions class="clone-picker__actions">
          <slot name="actions"></slot>
        </v-card-actions>
      </template>

      <template v-else>
        <div class="clone-picker__bar tax-picker__bar">
          <v-text-field
            ref="search"
            v-model="query"
            :placeholder="isChannels ? t('importerSearchChannels') : t('importerSearchModels')"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            outlined
            dense
            class="tax-picker__search"
            @keydown.enter.exact.prevent="pickOnlyMatch"
          ></v-text-field>
        </div>

        <v-card-text class="tax-picker__body">
          <!-- The filters, as groups of chips: first what the model is about, then — for channels —
               what the channel is. Each chip says how many rows the list would hold with it chosen,
               so a dead end is visible before it is walked into. -->
          <aside class="tax-facets" :aria-label="t('taxonomyFilters')">
            <div class="tax-facets__head">
              <span class="tax-facets__title">{{ t('taxonomyFilters') }}</span>
              <v-spacer></v-spacer>
              <v-btn v-if="filtering" x-small text color="primary" @click="clearFilters">
                {{ t('taxonomyClear') }}
              </v-btn>
            </div>
            <section v-for="group in facetGroups" :key="group.id" class="imp-group-facets">
              <div class="imp-group-facets__title">
                <v-icon x-small class="mr-1">{{ group.icon }}</v-icon>{{ group.title }}
              </div>
              <div v-if="rows.length > 0 && group.facets.length === 0" class="tax-facets__none">
                {{ group.none }}
              </div>
              <div v-for="facet in group.facets" :key="facet.key" class="tax-facet">
                <div class="tax-facet__label">{{ keyLabel(facet.key) }}</div>
                <div class="tax-facet__chips">
                  <v-chip
                    v-for="option in facetOptions(facet)"
                    :key="`${facet.key}_${option.value}`"
                    small
                    :outlined="!isChosen(facet.key, option.value)"
                    :color="isChosen(facet.key, option.value) ? 'primary' : undefined"
                    :disabled="option.count === 0 && !isChosen(facet.key, option.value)"
                    class="tax-facet__chip"
                    :class="{ 'tax-facet__chip--unclassified': option.value === unclassified }"
                    :aria-pressed="String(isChosen(facet.key, option.value))"
                    @click="toggleValue(facet.key, option.value)">
                    <v-icon v-if="isChosen(facet.key, option.value)" left x-small>mdi-check</v-icon>
                    <span class="tax-facet__name">{{ valueLabel(facet.key, option.value) }}</span>
                    <span class="tax-facet__count">{{ option.count }}</span>
                  </v-chip>
                  <button
                    v-if="facet.values.length > facetLimit"
                    type="button"
                    class="tax-facet__more"
                    @click="toggleExpanded(facet.key)">
                    {{ expanded[facet.key]
                      ? t('taxonomyShowLess')
                      : t('taxonomyShowAll', { count: facet.values.length }) }}
                  </button>
                </div>
              </div>
              <v-switch
                v-if="group.id === 'taxonomy'"
                v-model="onlyClassified"
                :label="t('taxonomyOnlyClassified')"
                color="primary"
                class="imp-switch"
                hide-details
                inset
                dense
              ></v-switch>
            </section>
          </aside>

          <section class="tax-results">
            <div v-if="loading && models.length === 0" class="clone-pick-empty">
              <v-progress-circular indeterminate color="primary" size="28" width="2"></v-progress-circular>
              <span class="clone-pick-empty__text">{{ t('taxonomyCatalogueLoading') }}</span>
            </div>
            <div v-else-if="rows.length === 0" class="clone-pick-empty">
              <v-icon large color="grey lighten-1">mdi-database-off-outline</v-icon>
              <span class="clone-pick-empty__text">{{ emptyText || t('noSourceModels') }}</span>
            </div>

            <template v-else>
              <div class="clone-pick-head tax-results__head">
                <v-checkbox
                  v-if="isChannels"
                  class="clone-pick-head__all"
                  color="primary"
                  dense
                  hide-details
                  :input-value="allPicked"
                  :indeterminate="somePicked"
                  :disabled="selectable.length === 0"
                  :aria-label="t('pickerSelectAll')"
                  :title="t('pickerSelectAll')"
                  @change="togglePickAll"
                ></v-checkbox>
                <div class="tax-results__meta">
                  <span class="clone-pick-head__count">
                    <template v-if="isChannels">
                      {{ $tc('components.ekrm.CreateModelCompose.pickerCount', ordered.length, { count: ordered.length }) }}
                      · {{ $tc('components.ekrm.CreateModelCompose.taxonomyModels', orderedModelCount, { count: orderedModelCount }) }}
                    </template>
                    <template v-else>
                      {{ $tc('components.ekrm.CreateModelCompose.taxonomyModels', ordered.length, { count: ordered.length }) }}
                    </template>
                    <span v-if="isChannels && selected.length > 0" class="clone-pick-head__picked">
                      · {{ t('pickerSelected', { count: selected.length }) }}
                    </span>
                  </span>
                  <!-- What the list is narrowed by, readable in one line and undoable one chip at a
                       time, above the rows it narrowed. -->
                  <div v-if="activeFilters.length > 0" class="tax-results__active">
                    <v-chip
                      v-for="filter in activeFilters"
                      :key="`active_${filter.key}_${filter.value}`"
                      x-small
                      close
                      color="primary"
                      outlined
                      :close-label="t('taxonomyRemoveFilter')"
                      @click:close="toggleValue(filter.key, filter.value)">
                      {{ keyLabel(filter.key) }}: {{ valueLabel(filter.key, filter.value) }}
                    </v-chip>
                  </div>
                </div>
                <v-spacer></v-spacer>
                <div v-if="isChannels" class="clone-pick-head__cols" aria-hidden="true">
                  <span class="clone-pick-head__col">{{ cardLabels.saturation }}</span>
                  <span class="clone-pick-head__col">{{ cardLabels.decay }}</span>
                </div>
              </div>

              <div v-if="ordered.length === 0" class="clone-pick-empty">
                <v-icon large color="grey lighten-1">mdi-filter-remove-outline</v-icon>
                <span class="clone-pick-empty__text">
                  {{ isChannels ? t('taxonomyNoMatch') : t('importerNoModelMatch') }}
                </span>
                <v-btn small text color="primary" @click="clearAll">{{ t('taxonomyClear') }}</v-btn>
              </div>

              <!-- ONE MODEL, as a row that is the answer when pressed. -->
              <template v-if="!isChannels">
                <div
                  v-for="row in rendered"
                  :key="`model_${row.id}`"
                  class="clone-pick imp-model"
                  :class="{ 'clone-pick--picked primary--text': row.id === activeModelId }"
                  role="button"
                  tabindex="0"
                  @click="pick(row)"
                  @keydown.space.exact.prevent="pick(row)"
                  @keydown.enter.exact.prevent="pick(row)">
                  <v-icon class="clone-pick__box" :color="row.id === activeModelId ? 'primary' : 'grey'">mdi-database</v-icon>
                  <div class="clone-pick__body">
                    <div class="clone-pick__line">
                      <span class="clone-pick__name text-truncate">{{ row.name }}</span>
                      <span v-if="row.id === activeModelId" class="clone-pick__tag">{{ t('importerCurrent') }}</span>
                    </div>
                    <div v-if="metaLine(row)" class="imp-meta text-truncate">{{ metaLine(row) }}</div>
                  </div>
                  <span v-if="noteFor(row)" class="imp-model__note">{{ noteFor(row) }}</span>
                  <v-icon small class="imp-model__go">mdi-chevron-right</v-icon>
                </div>
              </template>

              <!-- CHANNELS, under the model they were measured in. The model's own box ticks every
                   channel of it the filters leave, which is how a whole model is taken. -->
              <template v-for="(row, index) in (isChannels ? rendered : [])">
                <div
                  v-if="index === 0 || rendered[index - 1].modelId !== row.modelId"
                  :key="`group_${row.modelId}`"
                  class="imp-group"
                  :class="{ 'imp-group--first': index === 0 }">
                  <v-checkbox
                    class="imp-group__box"
                    color="primary"
                    dense
                    hide-details
                    :input-value="groupState(row.modelId) === 'all'"
                    :indeterminate="groupState(row.modelId) === 'some'"
                    :disabled="groupState(row.modelId) === 'none'"
                    :aria-label="t('importerAllChannels', { model: row.modelName })"
                    :title="t('importerAllChannels', { model: row.modelName })"
                    @change="toggleGroup(row.modelId)"
                  ></v-checkbox>
                  <v-icon small class="imp-group__icon">mdi-database</v-icon>
                  <div class="imp-group__body">
                    <span class="imp-group__name text-truncate">{{ row.modelName }}</span>
                    <span v-if="metaLine(row)" class="imp-meta text-truncate">{{ metaLine(row) }}</span>
                  </div>
                  <span class="imp-group__count">
                    {{ $tc('components.ekrm.CreateModelCompose.pickerCount', groupSize(row.modelId), { count: groupSize(row.modelId) }) }}
                  </span>
                </div>
                <div
                  :key="`pick_${row.id}`"
                  class="clone-pick tax-pick"
                  :class="rowClass(row)"
                  role="checkbox"
                  :aria-checked="row.added || !!selectedMap[row.id] ? 'true' : 'false'"
                  :aria-disabled="isLocked(row) ? 'true' : 'false'"
                  :tabindex="isLocked(row) ? -1 : 0"
                  :title="rowTitle(row)"
                  @click="toggle(row)"
                  @keydown.space.exact.prevent="toggle(row)"
                  @keydown.enter.exact.prevent="toggle(row)">
                  <v-icon class="clone-pick__box" :color="boxColor(row)">{{ boxIcon(row) }}</v-icon>
                  <div class="clone-pick__body">
                    <div class="clone-pick__line">
                      <span class="clone-pick__name text-truncate">{{ row.label }}</span>
                      <span v-if="row.added" class="clone-pick__tag">{{ t('pickerAdded') }}</span>
                      <span v-if="row.custom" class="clone-pick__tag tax-pick__custom text-truncate">{{ row.custom }}</span>
                    </div>
                    <div class="tax-pick__meta text-truncate">
                      <span v-if="row.trail.length > 0" class="tax-pick__trail">{{ breadcrumb(row) }}</span>
                      <span v-else class="tax-pick__unclassified">{{ t('taxonomyUnclassified') }}</span>
                    </div>
                    <div v-if="stateOf(row).reason" class="tax-pick__reason text-truncate">
                      <v-icon x-small color="warning darken-1" class="mr-1">mdi-alert-circle-outline</v-icon>{{ stateOf(row).reason }}
                    </div>
                  </div>
                  <div class="clone-pick__plots">
                    <template v-if="stateOf(row).kind === 'loading'">
                      <span class="clone-pick__svg tax-pick__skeleton"></span>
                      <span class="clone-pick__svg tax-pick__skeleton"></span>
                    </template>
                    <template v-else-if="stateOf(row).plot">
                      <svg v-if="stateOf(row).plot.path" class="clone-pick__svg" :viewBox="plotViewBox" preserveAspectRatio="none">
                        <title>{{ t('pickerPlotSaturation') }}</title>
                        <path class="clone-pick__line-path" :d="stateOf(row).plot.path.line" vector-effect="non-scaling-stroke"></path>
                      </svg>
                      <span v-else class="clone-pick__svg clone-pick__missing">{{ t('pickerNotMeasured') }}</span>
                      <svg v-if="stateOf(row).plot.decayBars.length > 0" class="clone-pick__svg" :viewBox="plotViewBox" preserveAspectRatio="none">
                        <title>{{ t('pickerPlotDecay') }}</title>
                        <rect
                          v-for="(bar, barIndex) in stateOf(row).plot.decayBars"
                          :key="`bar_${row.id}_${barIndex}`"
                          class="clone-pick__bar"
                          :x="bar.x" :y="bar.y" :width="bar.width" :height="bar.height"></rect>
                      </svg>
                      <span v-else class="clone-pick__svg clone-pick__missing">{{ t('pickerNotMeasured') }}</span>
                    </template>
                    <template v-else>
                      <span class="clone-pick__svg clone-pick__missing">—</span>
                      <span class="clone-pick__svg clone-pick__missing">—</span>
                    </template>
                  </div>
                </div>
              </template>

              <div v-if="ordered.length > rendered.length" class="tax-results__more">
                <span class="tax-results__showing">
                  {{ t('taxonomyShowing', { shown: rendered.length, total: ordered.length }) }}
                </span>
                <v-btn small text color="primary" @click="limit += pageSize">{{ t('taxonomyShowMore') }}</v-btn>
              </div>
            </template>
          </section>
        </v-card-text>

        <v-card-actions class="clone-picker__actions">
          <span v-if="isChannels && selected.length > 0" class="clone-picker__summary text-truncate">
            <template v-if="pendingModels > 0">
              <v-progress-circular indeterminate size="12" width="2" color="primary" class="mr-1"></v-progress-circular>
              {{ $tc('components.ekrm.CreateModelCompose.taxonomyReading', pendingModels, { count: pendingModels }) }}
            </template>
            <template v-else>{{ summary }}</template>
          </span>
          <span v-else-if="!isChannels" class="clone-picker__summary text-truncate">{{ t('importerPickHint') }}</span>
          <v-spacer></v-spacer>
          <v-btn text @click="close">{{ $t('components.TextBox.close') }}</v-btn>
          <v-btn
            v-if="isChannels"
            color="primary"
            depressed
            :disabled="!canAdd"
            :title="t('pickerAddHint')"
            @click="add">
            {{ $tc('components.ekrm.CreateModelCompose.pickerAdd', selected.length, { count: selected.length }) }}
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  CHANNEL_FACET_KEYS,
  compareTaxonomyRows,
  taxonomyBreadcrumb,
  taxonomyRows,
  TAXONOMY_FACET_LEVELS
} from '@/utils/channelTaxonomy.js'
import {
  facetCounts,
  filterRows,
  MODEL_FACET_KEYS,
  modelRows,
  UNCLASSIFIED
} from '@/utils/modelFacets.js'

const PAGE_SIZE = 40
const FACET_LIMIT = 8
const MODE_CHANNELS = 'channels'
const KEY_LABELS = {
  country: 'importerFacetCountry',
  client: 'importerFacetClient',
  brand: 'importerFacetBrand',
  category: 'importerFacetCategory',
  subcategory: 'importerFacetSubcategory'
}

export default {
  name: 'EkrmModelImporter',
  props: {
    value: { type: Boolean, default: false },
    // 'model' to choose one model, 'channels' to tick channels out of any number of them.
    mode: { type: String, default: 'model' },
    // The models to choose from — full model documents.
    models: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    title: { type: String, required: true },
    subtitle: { type: String, default: '' },
    icon: { type: String, default: 'mdi-database-import-outline' },
    // Said when there is no model at all to offer.
    emptyText: { type: String, default: '' },
    // How a country code reads, so it is shown — and searched — by its name.
    countryName: { type: Function, default: null },
    // A country as stored (a code or a name) folded into one key, so it is one chip and not two.
    countryKey: { type: Function, default: null },
    // A line of what a model has to say for itself on its row (model mode).
    modelNote: { type: Function, default: null },
    // The model the host is already carrying, marked on its row (model mode).
    activeModelId: { type: String, default: null },
    // The model chosen for the second step; the step shows while it is set (model mode).
    pickedModelId: { type: String, default: null },
    // Channels mode. Row ids (modelId::raw) already in the composition.
    added: { type: Object, default: () => ({}) },
    // Per model read so far: { channels: { raw: { path, decayBars } }, reasons: { raw: text }, blocked }.
    reads: { type: Object, default: () => ({}) },
    // Per model: 'queued' | 'loading' | 'ready' | 'failed'.
    status: { type: Object, default: () => ({}) },
    plotViewBox: { type: String, default: '0 0 1 1' },
    cardLabels: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      query: '',
      selection: {},
      onlyClassified: false,
      expanded: {},
      selected: [],
      limit: PAGE_SIZE,
      pageSize: PAGE_SIZE,
      facetLimit: FACET_LIMIT,
      unclassified: UNCLASSIFIED
    }
  },
  computed: {
    isChannels () {
      return this.mode === MODE_CHANNELS
    },
    onDetails () {
      return !this.isChannels && !!this.pickedModelId
    },
    facetKeys () {
      return this.isChannels ? CHANNEL_FACET_KEYS : MODEL_FACET_KEYS
    },
    modelsById () {
      const map = {}
      this.models.forEach(model => { if (model && model.id) map[model.id] = model })
      return map
    },
    allRows () {
      return this.isChannels
        ? taxonomyRows(this.models, this.labelFor, this.countryKey)
        : modelRows(this.models, this.labelFor, this.countryKey)
    },
    rows () {
      return this.isChannels && this.onlyClassified ? this.allRows.filter(row => row.classified) : this.allRows
    },
    rowsById () {
      const map = {}
      this.allRows.forEach(row => { map[row.id] = row })
      return map
    },
    pickedRow () {
      if (!this.pickedModelId) return null
      const model = this.modelsById[this.pickedModelId]
      return model ? modelRows([model], this.labelFor, this.countryKey)[0] : null
    },
    facets () {
      return facetCounts(this.rows, this.selection, this.query, this.facetKeys)
    },
    facetGroups () {
      const visible = keys => this.facets.filter(facet => facet.visible && keys.indexOf(facet.key) !== -1)
      const groups = [{
        id: 'model',
        icon: 'mdi-database',
        title: this.t('importerModelFacets'),
        none: this.t('importerNoMetadata'),
        facets: visible(MODEL_FACET_KEYS)
      }]
      if (this.isChannels) {
        groups.push({
          id: 'taxonomy',
          icon: 'mdi-file-tree-outline',
          title: this.t('importerTaxonomyFacets'),
          none: this.t('taxonomyNoneYet'),
          facets: visible(TAXONOMY_FACET_LEVELS)
        })
      }
      return groups
    },
    filtering () {
      return this.activeFilters.length > 0
    },
    activeFilters () {
      const filters = []
      this.facetKeys.forEach(key => {
        ;(this.selection[key] || []).forEach(value => filters.push({ key: key, value: value }))
      })
      return filters
    },
    // Model mode: by name. Channels mode: grouped by model, and inside a model what can still be
    // chosen before what is already in the composition. `added` is copied onto a new object per
    // row rather than written onto the cached one, so a channel placed behind the dialog moves its
    // row without re-reading anything.
    ordered () {
      const filtered = filterRows(this.rows, this.selection, this.query, this.facetKeys)
      if (!this.isChannels) {
        return filtered.slice().sort((a, b) => a.name.localeCompare(b.name))
      }
      return filtered
        .map(row => Object.assign({}, row, { added: !!this.added[row.id] }))
        .sort((a, b) => {
          const byModel = a.modelName.localeCompare(b.modelName) || String(a.modelId).localeCompare(String(b.modelId))
          if (byModel !== 0) return byModel
          if (a.added !== b.added) return a.added ? 1 : -1
          return compareTaxonomyRows(a, b)
        })
    },
    orderedModelCount () {
      const ids = {}
      this.ordered.forEach(row => { ids[row.modelId] = true })
      return Object.keys(ids).length
    },
    // Every row the filters leave, per model — what a model's own box acts on and counts.
    orderedByModel () {
      const map = {}
      this.ordered.forEach(row => {
        if (!map[row.modelId]) map[row.modelId] = []
        map[row.modelId].push(row)
      })
      return map
    },
    rendered () {
      return this.ordered.slice(0, this.limit)
    },
    selectedMap () {
      const map = {}
      this.selected.forEach(id => { map[id] = true })
      return map
    },
    selectable () {
      return this.rendered.filter(row => !this.isLocked(row))
    },
    allPicked () {
      return this.selectable.length > 0 && this.selectable.every(row => this.selectedMap[row.id])
    },
    somePicked () {
      return !this.allPicked && this.selectable.some(row => this.selectedMap[row.id])
    },
    // The models the rows on screen and the rows ticked need read, so their curves can be drawn and
    // the ticked ones placed.
    wantedModels () {
      if (!this.isChannels) return []
      const ids = {}
      this.rendered.forEach(row => { ids[row.modelId] = true })
      this.selected.forEach(id => {
        const row = this.rowsById[id]
        if (row) ids[row.modelId] = true
      })
      return Object.keys(ids)
    },
    pendingModels () {
      const ids = {}
      this.selected.forEach(id => {
        const row = this.rowsById[id]
        if (row && this.stateOf(row).kind === 'loading') ids[row.modelId] = true
      })
      return Object.keys(ids).length
    },
    readyPairs () {
      return this.selected
        .map(id => this.rowsById[id])
        .filter(row => row && !this.added[row.id] && this.stateOf(row).kind === 'ready')
        .map(row => ({ modelId: row.modelId, raw: row.raw }))
    },
    canAdd () {
      return this.isChannels && this.pendingModels === 0 && this.readyPairs.length > 0
    },
    summary () {
      const models = {}
      this.readyPairs.forEach(pair => { models[pair.modelId] = true })
      const modelCount = Object.keys(models).length
      return this.t('taxonomySummary', {
        channels: this.$tc('components.ekrm.CreateModelCompose.pickerCount', this.readyPairs.length, { count: this.readyPairs.length }),
        models: this.$tc('components.ekrm.CreateModelCompose.taxonomyModels', modelCount, { count: modelCount })
      })
    }
  },
  watch: {
    // The filters and the search outlive a visit: a reader who narrowed the catalogue down to one
    // market is usually coming back for more of it. The ticks do not.
    value (open) {
      if (!open) return
      this.selected = []
      this.limit = PAGE_SIZE
      this.requestModels()
      this.focusSearch()
    },
    pickedModelId (id) {
      if (!id) this.focusSearch()
    },
    wantedModels () {
      this.requestModels()
    },
    query () {
      this.limit = PAGE_SIZE
    },
    selection () {
      this.limit = PAGE_SIZE
    },
    onlyClassified () {
      this.limit = PAGE_SIZE
    },
    // A tick on a channel that turns out, once its model is read, not to be placeable is taken off
    // rather than left standing on a row that cannot be added.
    reads () {
      this.dropLocked()
    },
    status () {
      this.dropLocked()
    },
    added () {
      this.dropLocked()
    }
  },
  methods: {
    t (key, values) {
      return this.$t(`components.ekrm.CreateModelCompose.${key}`, values)
    },
    // VDialog takes the focus on the tick after it opens, so a focus taken any earlier is taken and
    // then given back.
    focusSearch () {
      setTimeout(() => {
        const field = this.$refs.search
        if (this.value && !this.onDetails && field && field.focus) field.focus()
      }, 220)
    },
    labelFor (key, value) {
      if (key === 'country' && this.countryName) return this.countryName(value) || value
      return value
    },
    keyLabel (key) {
      if (KEY_LABELS[key]) return this.t(KEY_LABELS[key])
      return this.$t(`components.ModelSettings.taxonomyLevel${key}`)
    },
    valueLabel (key, value) {
      if (value === UNCLASSIFIED) {
        return TAXONOMY_FACET_LEVELS.indexOf(key) !== -1 ? this.t('taxonomyUnclassified') : this.t('importerNotSet')
      }
      return this.labelFor(key, value)
    },
    // What a model is about, in one line: its country, client, brand, category and sub-category.
    metaLine (row) {
      const parts = []
      MODEL_FACET_KEYS.forEach(key => {
        const values = row.facets[key] || []
        if (values.length > 0) parts.push(values.map(value => this.labelFor(key, value)).join(', '))
      })
      return parts.join(' · ')
    },
    noteFor (row) {
      const model = this.modelsById[row.modelId]
      return model && this.modelNote ? this.modelNote(model) : ''
    },
    breadcrumb (row) {
      return taxonomyBreadcrumb(row)
    },
    isChosen (key, value) {
      const chosen = this.selection[key]
      return !!chosen && chosen.indexOf(value) !== -1
    },
    // The first few values of a key, plus any chosen one past them, so a choice never disappears
    // behind "Show all".
    facetOptions (facet) {
      if (this.expanded[facet.key] || facet.values.length <= FACET_LIMIT) return facet.values
      return facet.values.filter((option, index) => index < FACET_LIMIT || this.isChosen(facet.key, option.value))
    },
    toggleExpanded (key) {
      this.$set(this.expanded, key, !this.expanded[key])
    },
    toggleValue (key, value) {
      const next = Object.assign({}, this.selection)
      const chosen = (next[key] || []).slice()
      const at = chosen.indexOf(value)
      if (at === -1) chosen.push(value)
      else chosen.splice(at, 1)
      if (chosen.length > 0) next[key] = chosen
      else delete next[key]
      this.selection = next
    },
    clearFilters () {
      this.selection = {}
    },
    clearAll () {
      this.selection = {}
      this.query = ''
      this.onlyClassified = false
    },

    // --- Model mode ---------------------------------------------------------

    pick (row) {
      this.$emit('pick', row.modelId)
    },
    // Enter in the search takes the model when the search has narrowed the list down to one.
    pickOnlyMatch () {
      if (!this.isChannels && this.ordered.length === 1) this.pick(this.ordered[0])
    },

    // --- Channels mode ------------------------------------------------------

    requestModels () {
      if (!this.value || !this.isChannels) return
      const needed = this.wantedModels.filter(id => !this.reads[id] && !this.status[id])
      if (needed.length > 0) this.$emit('need-models', needed)
    },
    // What a row can offer, off what the composer has read of its model so far.
    stateOf (row) {
      const read = this.reads[row.modelId]
      if (!read) {
        if (this.status[row.modelId] === 'failed') {
          return { kind: 'unavailable', reason: this.t('taxonomyReadFailed'), plot: null }
        }
        return { kind: 'loading', reason: null, plot: null }
      }
      if (read.blocked) return { kind: 'unavailable', reason: read.blocked, plot: null }
      const plot = read.channels[row.raw]
      if (plot) return { kind: 'ready', reason: null, plot: plot }
      return {
        kind: 'unavailable',
        reason: read.reasons[row.raw] || this.t('taxonomyNotInReport'),
        plot: null
      }
    },
    isLocked (row) {
      return !!this.added[row.id] || this.stateOf(row).kind === 'unavailable'
    },
    rowClass (row) {
      const locked = this.isLocked(row)
      return {
        'clone-pick--picked primary--text': !locked && !!this.selectedMap[row.id],
        'clone-pick--added': row.added,
        'tax-pick--unavailable': !row.added && locked
      }
    },
    rowTitle (row) {
      if (row.added) return this.t('pickerAlready')
      return this.stateOf(row).reason
    },
    boxIcon (row) {
      if (row.added) return 'mdi-check-circle'
      if (this.isLocked(row)) return 'mdi-checkbox-blank-off-outline'
      return this.selectedMap[row.id] ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
    },
    boxColor (row) {
      if (this.isLocked(row)) return 'grey lighten-1'
      return this.selectedMap[row.id] ? 'primary' : 'grey'
    },
    toggle (row) {
      if (this.isLocked(row)) return
      const at = this.selected.indexOf(row.id)
      if (at === -1) this.selected.push(row.id)
      else this.selected.splice(at, 1)
    },
    groupSize (modelId) {
      return (this.orderedByModel[modelId] || []).length
    },
    groupSelectable (modelId) {
      return (this.orderedByModel[modelId] || []).filter(row => !this.isLocked(row))
    },
    // 'none' when nothing of the model can be ticked, else how much of what can be is.
    groupState (modelId) {
      const rows = this.groupSelectable(modelId)
      if (rows.length === 0) return 'none'
      const ticked = rows.filter(row => this.selectedMap[row.id]).length
      if (ticked === rows.length) return 'all'
      return ticked > 0 ? 'some' : 'empty'
    },
    toggleGroup (modelId) {
      this.toggleRows(this.groupSelectable(modelId).map(row => row.id), this.groupState(modelId) === 'all')
    },
    // Like the head's box, it acts on what is on screen and keeps ticks the filters hide.
    togglePickAll () {
      this.toggleRows(this.selectable.map(row => row.id), this.allPicked)
    },
    toggleRows (ids, untick) {
      if (untick) {
        this.selected = this.selected.filter(id => ids.indexOf(id) === -1)
        return
      }
      const next = this.selected.slice()
      ids.forEach(id => { if (next.indexOf(id) === -1) next.push(id) })
      this.selected = next
    },
    dropLocked () {
      const kept = this.selected.filter(id => {
        const row = this.rowsById[id]
        return row && !this.isLocked(row)
      })
      if (kept.length !== this.selected.length) this.selected = kept
    },
    add () {
      if (!this.canAdd) return
      this.$emit('add', this.readyPairs)
      this.selected = []
    },
    close () {
      this.$emit('input', false)
    }
  }
}
</script>

<style scoped>
  /* The composer's picker chrome, repeated: scoped styles do not reach a child, and this dialog is
     meant to read as part of the same page. */
  .v-card__title.clone-picker__title {
    padding: 14px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    flex: none;
    word-break: normal;
  }
  .clone-picker__heading {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .clone-picker__subtitle {
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1.4;
    color: rgba(0, 0, 0, 0.55);
    letter-spacing: 0;
  }
  .clone-picker__summary {
    display: flex;
    align-items: center;
    min-width: 0;
    font-size: 0.78rem;
    color: rgba(0, 0, 0, 0.6);
    padding-left: 4px;
  }
  .clone-picker__bar {
    flex: none;
    padding: 12px 16px;
    background-color: #fafafa;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
  .v-card__actions.clone-picker__actions {
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    padding: 8px 12px;
  }
  .clone-pick-head {
    position: sticky;
    top: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 12px 13px 10px 0;
    margin-bottom: 8px;
    background-color: #ffffff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
  .clone-pick-head__all {
    margin: 0 4px 0 0;
    padding: 0;
    flex: none;
  }
  .clone-pick-head__count {
    font-size: 0.75rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.55);
    min-width: 0;
  }
  .clone-pick-head__picked {
    color: rgba(0, 0, 0, 0.75);
    font-weight: 500;
  }
  .clone-pick-head__cols {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: none;
    margin-left: 12px;
  }
  .clone-pick-head__col {
    width: 96px;
    text-align: center;
    font-size: 0.65rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.45);
  }
  .clone-pick {
    position: relative;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    background-color: #ffffff;
    cursor: pointer;
    transition: border-color 0.2s, background-color 0.2s;
  }
  .clone-pick + .clone-pick {
    margin-top: 6px;
  }
  .clone-pick:hover {
    border-color: rgba(0, 0, 0, 0.3);
    background-color: rgba(0, 0, 0, 0.02);
  }
  .clone-pick:focus-visible {
    outline: 1px solid rgba(0, 0, 0, 0.42);
    outline-offset: 2px;
  }
  .clone-pick--picked {
    border-color: currentColor;
  }
  .clone-pick--picked::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    border-radius: 4px;
    background-color: currentColor;
    opacity: 0.06;
    pointer-events: none;
  }
  .clone-pick--added {
    border-style: dashed;
    background-color: #fafafa;
    cursor: default;
  }
  .clone-pick--added:hover {
    border-color: rgba(0, 0, 0, 0.12);
    background-color: #fafafa;
  }
  .clone-pick__box {
    flex: none;
  }
  .clone-pick__body {
    flex: 1 1 auto;
    min-width: 0;
  }
  .clone-pick__line {
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
  .clone-pick__name {
    font-size: 0.875rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.87);
    min-width: 0;
  }
  .clone-pick--added .clone-pick__name {
    color: rgba(0, 0, 0, 0.55);
  }
  .clone-pick__tag {
    flex: none;
    font-size: 0.6rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(0, 0, 0, 0.45);
    border: 1px solid rgba(0, 0, 0, 0.16);
    border-radius: 3px;
    padding: 0 5px;
    line-height: 1.5;
  }
  .clone-pick__plots {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: none;
  }
  .clone-pick__svg {
    width: 96px;
    height: 28px;
    display: block;
    flex: none;
  }
  .clone-pick__line-path {
    fill: none;
    stroke: #4B67B8;
    stroke-width: 1.5;
  }
  .clone-pick__bar {
    fill: #7E57C2;
  }
  .clone-pick--added .clone-pick__plots {
    opacity: 0.5;
  }
  .clone-pick__missing {
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed rgba(0, 0, 0, 0.14);
    border-radius: 3px;
    font-size: 0.6rem;
    letter-spacing: 0.02em;
    text-align: center;
    line-height: 1.1;
    color: rgba(0, 0, 0, 0.35);
  }
  .clone-pick-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 240px;
    margin-top: 12px;
    padding: 24px;
    text-align: center;
    border: 1px dashed rgba(0, 0, 0, 0.18);
    border-radius: 4px;
  }
  .clone-pick-empty__text {
    max-width: 340px;
    font-size: 0.8rem;
    line-height: 1.5;
    color: rgba(0, 0, 0, 0.5);
  }

  .tax-picker__bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 20px;
  }
  .tax-picker__search {
    flex: 1 1 320px;
    background-color: #ffffff;
  }

  /* Two columns that scroll apart: the levels stay where they are while the list is read. The body
     is given a height because a scrollable dialog otherwise sizes it to its content, and the
     columns would have nothing to scroll inside. */
  .v-card__text.tax-picker__body {
    display: flex;
    padding: 0;
    height: 68vh;
    overflow: hidden;
    color: inherit;
  }
  .tax-facets {
    flex: none;
    width: 280px;
    overflow-y: auto;
    padding: 12px 16px 16px;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    background-color: #fcfcfc;
  }
  .tax-facets__head {
    display: flex;
    align-items: center;
    min-height: 28px;
    margin-bottom: 4px;
  }
  .tax-facets__title {
    font-size: 0.75rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.55);
  }
  .tax-facets__none {
    font-size: 0.78rem;
    line-height: 1.5;
    color: rgba(0, 0, 0, 0.5);
    margin-top: 8px;
  }
  .tax-facet {
    padding: 10px 0 12px;
  }
  .tax-facet + .tax-facet {
    border-top: 1px solid rgba(0, 0, 0, 0.06);
  }
  .tax-facet__label {
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.55);
    margin-bottom: 8px;
  }
  .tax-facet__chips {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }
  .v-chip.tax-facet__chip {
    margin: 0;
    max-width: 100%;
  }
  .tax-facet__name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .tax-facet__count {
    margin-left: 6px;
    font-size: 0.68rem;
    opacity: 0.6;
  }
  .tax-facet__chip--unclassified .tax-facet__name {
    font-style: italic;
  }
  .tax-facet__more {
    font-size: 0.72rem;
    color: var(--v-primary-base, #1976d2);
    padding: 2px 4px;
  }
  .tax-facet__more:hover {
    text-decoration: underline;
  }

  .tax-results {
    flex: 1 1 auto;
    min-width: 0;
    overflow-y: auto;
    padding: 0 16px 16px;
  }
  .tax-results__meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .tax-results__active {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
  /* Vuetify gives a chip's close icon 18px whatever the chip's size, which on an x-small chip is a
     circle taller than the text beside it. The icon is VChip's own markup, out of reach of a scoped
     rule without ::v-deep. */
  .tax-results__active .v-chip.v-size--x-small {
    height: 20px;
    padding: 0 6px 0 8px;
    font-size: 0.68rem;
    letter-spacing: 0;
    text-transform: none;
  }
  .tax-results__active ::v-deep .v-chip__close.v-icon {
    font-size: 13px !important;
    width: 13px;
    height: 13px;
    max-width: 13px;
    max-height: 13px;
    margin-left: 4px;
    margin-right: 0;
    opacity: 0.7;
  }
  .tax-results__active ::v-deep .v-chip__close.v-icon:hover {
    opacity: 1;
  }
  .tax-results__more {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 12px;
  }
  .tax-results__showing {
    font-size: 0.72rem;
    color: rgba(0, 0, 0, 0.45);
  }

  .tax-pick__meta {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 2px;
    font-size: 0.72rem;
    color: rgba(0, 0, 0, 0.55);
    min-width: 0;
  }
  .tax-pick__trail {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgba(0, 0, 0, 0.7);
  }
  .tax-pick__unclassified {
    font-style: italic;
    color: rgba(0, 0, 0, 0.4);
  }
  .clone-pick__tag.tax-pick__custom {
    max-width: 160px;
    text-transform: none;
    letter-spacing: 0;
  }
  .tax-pick__reason {
    margin-top: 2px;
    font-size: 0.7rem;
    color: rgba(0, 0, 0, 0.55);
  }
  .tax-pick--unavailable {
    cursor: default;
    background-color: #fafafa;
  }
  .tax-pick--unavailable:hover {
    border-color: rgba(0, 0, 0, 0.12);
    background-color: #fafafa;
  }
  .tax-pick--unavailable .clone-pick__name {
    color: rgba(0, 0, 0, 0.45);
  }
  .tax-pick__skeleton {
    border-radius: 3px;
    background: linear-gradient(90deg, rgba(0, 0, 0, 0.05) 25%, rgba(0, 0, 0, 0.1) 50%, rgba(0, 0, 0, 0.05) 75%);
    background-size: 200% 100%;
    animation: tax-shimmer 1.2s ease-in-out infinite;
  }
  @keyframes tax-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* The two groups of filters, what the model is about and what the channel is, told apart by a
     heading and a rule. */
  .imp-group-facets + .imp-group-facets {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 2px solid rgba(0, 0, 0, 0.08);
  }
  .imp-group-facets__title {
    display: flex;
    align-items: center;
    padding-top: 6px;
    font-size: 0.72rem;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.75);
  }
  .imp-switch {
    margin-top: 4px;
    padding-top: 0;
  }
  .imp-meta {
    display: block;
    margin-top: 2px;
    font-size: 0.72rem;
    color: rgba(0, 0, 0, 0.55);
    min-width: 0;
  }

  /* A model row: the whole row is the button. */
  .imp-model__note {
    flex: none;
    max-width: 35%;
    font-size: 0.72rem;
    color: rgba(0, 0, 0, 0.5);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .imp-model__go {
    flex: none;
    color: rgba(0, 0, 0, 0.3);
  }
  .imp-model:hover .imp-model__go {
    color: rgba(0, 0, 0, 0.6);
  }

  /* The header a model's channels stand under. */
  .imp-group {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 18px 0 6px;
    padding: 0 12px 4px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }
  .imp-group--first {
    margin-top: 4px;
  }
  .imp-group__box {
    flex: none;
    margin: 0 0 0 1px;
    padding: 0;
  }
  .imp-group__icon {
    flex: none;
    color: rgba(0, 0, 0, 0.4);
  }
  .imp-group__body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
  }
  .imp-group__name {
    font-size: 0.82rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.8);
  }
  .imp-group__count {
    flex: none;
    font-size: 0.7rem;
    color: rgba(0, 0, 0, 0.45);
  }

  /* The second step: the chosen model, and the host's question under it. */
  .imp-chosen {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .imp-chosen__back {
    flex: none;
  }
  .imp-chosen__icon {
    flex: none;
    color: rgba(0, 0, 0, 0.4);
  }
  .imp-chosen__body {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .imp-chosen__name {
    font-size: 0.9rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.87);
  }
  .v-card__text.imp-details {
    padding: 16px;
    color: inherit;
  }

  /* A phone: the levels above the list rather than beside it, and the plots dropped — at that width
     the name is the only thing a row has room to say. */
  @media (max-width: 700px) {
    .v-card__text.tax-picker__body {
      flex-direction: column;
      height: auto;
      overflow-y: auto;
    }
    .tax-facets {
      width: auto;
      border-right: none;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
      overflow-y: visible;
    }
    .tax-results {
      overflow-y: visible;
    }
    .clone-pick__plots,
    .clone-pick-head__cols {
      display: none;
    }
  }
</style>
